import React, { useState, useEffect } from "react";
import { Table, Button, Modal, Form, Spinner, Alert } from "react-bootstrap";
import { PencilSquare, ArrowClockwise } from "react-bootstrap-icons";
import {
  getAPI,
  uploadFile,
  postAPI,
  putAPI,
  deleteAPI,
} from "../../services/apiService";
import {
  GET_VENDORS_URL,
  UPLOAD_IMAGE_URL,
  DELETE_FILE_URL,
  SAVE_CSV_METADATA_URL,
  GET_ALL_CSV_METADATA_URL,
  UPDATE_CSV_METADATA_URL,
} from "../../services/apiConstant";

const VendorProductEntry = (props) => {
  const user = props.user;
  const [showModal, setShowModal] = useState(false);
  const [vendorName, setVendorName] = useState("");
  const [selectedVendorName, setSelectedVendorName] = useState("");
  const [templateType, setTemplateType] = useState(""); // NEW STATE
  const [file, setFile] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submissions, setSubmissions] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [isRefreshing, setIsRefreshing] = useState(false);

  useEffect(() => {
    fetchVendors();
    fetchAllCSVMetadata();
  }, []);

  const fetchVendors = async () => {
    try {
      const response = await getAPI(GET_VENDORS_URL);
      setVendors(response);
    } catch (err) {
      console.error("Failed to fetch vendors", err);
    }
  };

  const fetchAllCSVMetadata = async () => {
    try {
      setIsRefreshing(true);
      const response = await getAPI(GET_ALL_CSV_METADATA_URL);
      setSubmissions(response || []);
    } catch (error) {
      console.error("Failed to fetch CSV metadata", error);
    } finally {
      setIsRefreshing(false);
    }
  };

  const uploadCSV = async () => {
    if (!file) {
      console.error("File is missing");
      return;
    }

    const formData = new FormData();
    formData.append("file", file);
    formData.append("folderName", getFolderName());
    formData.append("bucketName", "dhansheera-vendor");
    formData.append("fileName", file.name);
    formData.append("updatedBy", user?.claims?.name || "Unknown User");

    try {
      const uploadFileResponse = await uploadFile(UPLOAD_IMAGE_URL, formData, true);
      return uploadFileResponse;
    } catch (error) {
      console.error("Failed to upload CSV:", error);
      throw error;
    }
  };

  const getFolderName = () => {
    const selectedVendor = vendors.find((v) => v.uid === vendorName);
    const _vendorName = selectedVendorName || selectedVendor?.displayName;
    const vendorUID = vendorName || selectedVendor?.uid;
    return `${_vendorName}_${vendorUID}`;
  };

  const deleteCSVFolder = async () => {
    const folderPath = getFolderName();

    const body = {
      bucketName: "dhansheera-vendor",
      folderPath: `${folderPath}/`,
    };

    try {
      const response = await deleteAPI(DELETE_FILE_URL, body);
      return response;
    } catch (error) {
      console.error("Failed to delete folder:", error);
    }
  };

  const saveCSVMetadata = async (metadata) => {
    try {
      await postAPI(SAVE_CSV_METADATA_URL, metadata);
    } catch (error) {
      console.error("Error saving metadata:", error);
      throw error;
    }
  };

  const updateCSVMetadata = async (id, updatedMetadata) => {
    try {
      const body = { ...updatedMetadata, id };
      await putAPI(`${UPDATE_CSV_METADATA_URL}`, body);
    } catch (error) {
      console.error("Error updating metadata:", error);
      throw error;
    }
  };

  const handleShowModal = (item = null) => {
    setEditingId(item?.id || null);
    setShowModal(true);

    if (item) {
      const matchedVendor = vendors.find(v => v.displayName === item.vendorName);
      setVendorName(matchedVendor?.uid || "");
      setSelectedVendorName(item.vendorName || "");
      setTemplateType(item.templateType || ""); // Restore template type if editing
    } else {
      setVendorName("");
      setSelectedVendorName("");
      setTemplateType("");
    }

    setFile(null);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setVendorName("");
    setFile(null);
    setEditingId(null);
    setSelectedVendorName("");
    setTemplateType("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    const selectedVendor = vendors.find((v) => v.uid === vendorName);
    const _vendorName = selectedVendorName || selectedVendor?.displayName;
    const vendorUID = selectedVendor?.uid;

    const vendorAlreadySubmitted = submissions.some(
      (item) => item.vendorName === _vendorName
    );

    const vendorProcessing = submissions.some(
      (item) => item.vendorName === _vendorName && item.status === "Processing"
    );

    if (vendorProcessing) {
      alert("Vendor CSV is currently processing. Please wait.");
      setIsSubmitting(false);
      return;
    }

    try {
      if (!editingId && vendorAlreadySubmitted) {
        throw new Error("This vendor already exists. Please update the existing entry.");
      }

      if (editingId) {
        await deleteCSVFolder(); // Remove old folder before upload
      }

      const uploadResponse = await uploadCSV();
      const uploadedFileUrl = uploadResponse?.imagePublicUrl;
      if (!uploadedFileUrl) throw new Error("Missing uploaded file URL");

      const metadata = {
        fileUrl: uploadedFileUrl,
        filename: file?.name || "products.csv",
        lastSyncTime: new Date().toISOString(),
        updatedBy: user?.claims?.name || "Unknown User",
        status: "Pending",
        bucketname: "dhansheera-vendor",
        vendorUid: vendorUID,
        templateType: templateType || "Default", // Default fallback
      };

      if (editingId) {
        await updateCSVMetadata(editingId, metadata);
      } else {
        metadata.vendorName = _vendorName;
        metadata.submittedBy = user?.claims?.name || "Unknown User";
        await saveCSVMetadata(metadata);
      }

      await fetchAllCSVMetadata();
      handleCloseModal();
    } catch (error) {
      console.error("Error during upload/save:", error);
      alert(error.message || "Something went wrong.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const anyProcessing = submissions.some((item) => item.status === "Processing");

  const selectedVendorProcessing =
    editingId &&
    submissions.find((item) => item.id === editingId)?.status === "Processing";

  const processingVendors = new Set(
    submissions.filter((item) => item.status === "Processing").map((item) => item.vendorName)
  );

  const selectedVendor = vendors.find((v) => v.uid === vendorName);
  const vendorAlreadySubmitted =
    selectedVendor && submissions.some((item) => item.vendorName === selectedVendor.displayName);

  return (
    <div className="container mt-4">
      {/* Header & Button */}
      <div className="d-flex justify-content-between align-items-center">
        <h2>Product Catalogue By Vendors</h2>
        <div className="d-flex align-items-center">
          <Button
            variant="outline-secondary"
            onClick={fetchAllCSVMetadata}
            className="me-2"
            title="Refresh"
            disabled={isRefreshing}
          >
            {isRefreshing ? <Spinner animation="border" size="sm" /> : <ArrowClockwise />}
          </Button>
          <Button
            variant="primary"
            onClick={() => handleShowModal()}
            disabled={anyProcessing}
            title={anyProcessing ? "Wait until current processing completes" : ""}
          >
            Upload New Product CSV
          </Button>
        </div>
      </div>

      {/* Global processing alert */}
      {anyProcessing && (
        <Alert variant="info" className="mt-3">
          <Spinner animation="border" size="sm" className="me-1" />
          One or more vendors have a CSV upload currently <strong>processing</strong>.
        </Alert>
      )}

      {/* Table */}
      <Table striped bordered hover responsive className="mt-3">
        <thead>
          <tr>
            <th>Vendor Name</th>
            <th>Filename</th>
            <th>Categories</th>
            <th>Products</th>
            <th>Template</th>
            <th>Submitted By</th>
            <th>Last Sync Time</th>
            <th>Status</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {submissions.length > 0 ? (
            submissions.map((item, index) => (
              <tr key={item.id || index}>
                <td>{item.vendorName}</td>
                <td>
                  <a href={item.fileUrl} target="_blank" rel="noopener noreferrer">
                    {item.filename}
                  </a>
                </td>
                <td>{item.totalCatalogue || 0}</td>
                <td>{item.totalProduct || 0}</td>
                <td>{item.templateType || "N/A"}</td>
                <td>{item.submittedBy}</td>
                <td>{new Date(item.lastSyncTime).toLocaleString()}</td>
                <td>
                  {item.status === "Processing" ? (
                    <>
                      <Spinner animation="border" size="sm" className="me-1" />
                      Processing
                    </>
                  ) : (
                    item.status
                  )}
                </td>
                <td>
                  <Button
                    variant="outline-primary"
                    size="sm"
                    onClick={() => handleShowModal(item)}
                    disabled={processingVendors.has(item.vendorName)}
                    title={
                      processingVendors.has(item.vendorName)
                        ? "This vendor's CSV is processing"
                        : ""
                    }
                  >
                    <PencilSquare size={16} /> Update
                  </Button>
                </td>
              </tr>
            ))
          ) : (
            <tr>
              <td colSpan="9" className="text-center">No submissions found</td>
            </tr>
          )}
        </tbody>
      </Table>

      {/* Modal */}
      <Modal show={showModal} onHide={handleCloseModal} centered>
        <Modal.Header closeButton>
          <Modal.Title>{editingId ? "Update CSV" : "Upload New CSV"}</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSubmit}>
          <Modal.Body>
            {!editingId && (
              <>
                <Form.Group controlId="formVendor">
                  <Form.Label>Vendor Name</Form.Label>
                  <Form.Select
                    value={vendorName}
                    onChange={(e) => setVendorName(e.target.value)}
                    required
                    disabled={anyProcessing}
                  >
                    <option value="">Select Vendor</option>
                    {vendors?.map((vendor) => (
                      <option key={vendor.uid} value={vendor.uid}>
                        {vendor.displayName}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
                {vendorAlreadySubmitted && (
                  <Alert variant="warning" className="mt-2">
                    This vendor has already submitted a CSV. Please use the update option instead.
                  </Alert>
                )}
              </>
            )}

            {/* Template Type Dropdown */}
            <Form.Group controlId="formTemplateType" className="mt-3">
              <Form.Label>Template Type</Form.Label>
              <Form.Select
                value={templateType}
                onChange={(e) => setTemplateType(e.target.value)}
                required
              >
                <option value="">Select Template</option>
                <option value="Default">Default</option>
                <option value="Shopify">Shopify</option>
              </Form.Select>
            </Form.Group>

            {/* File Upload */}
            <Form.Group controlId="formFile" className="mt-3">
              <Form.Label>{editingId ? "Upload Updated CSV File" : "Upload CSV File"}</Form.Label>
              <Form.Control
                type="file"
                accept=".csv"
                onChange={(e) => setFile(e.target.files[0])}
                required
                disabled={anyProcessing || selectedVendorProcessing}
              />
            </Form.Group>

            {selectedVendorProcessing && (
              <Alert variant="info" className="mt-3">
                <Spinner animation="border" size="sm" className="me-1" />
                This vendor's CSV is currently <strong>processing</strong>.
              </Alert>
            )}
          </Modal.Body>

          <Modal.Footer>
            <Button variant="secondary" onClick={handleCloseModal} disabled={isSubmitting}>
              Cancel
            </Button>
            <Button
              variant="primary"
              type="submit"
              disabled={
                isSubmitting ||
                (!editingId && vendorAlreadySubmitted) ||
                anyProcessing ||
                selectedVendorProcessing
              }
            >
              {isSubmitting ? (
                <>
                  <Spinner animation="border" size="sm" className="me-1" /> Submitting...
                </>
              ) : editingId ? (
                "Update"
              ) : (
                "Submit"
              )}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </div>
  );
};

export default VendorProductEntry;
